import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { AlertasData } from "../types";
import { statusTone } from "../format";
import ErrorState from "./ErrorState";
import { IconMail, IconRefresh, IconCheck } from "../icons";

interface Props {
  onLoaded?: (d: AlertasData) => void; // sincroniza o banner do App
}

/**
 * Tela "Alertas de Estoque": lista os itens abaixo do mínimo/zerados e permite
 * enviar o aviso por e-mail (almoxarifado/compras/operação) sob demanda.
 */
export default function AlertasView({ onLoaded }: Props) {
  const [data, setData] = useState<AlertasData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ tipo: "ok" | "err"; texto: string } | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.alertas.get();
      setData(d);
      onLoaded?.(d);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [onLoaded]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function notificar() {
    setEnviando(true);
    setMsg(null);
    try {
      const r = await api.alertas.notificar();
      if (r.total === 0) setMsg({ tipo: "ok", texto: r.mensagem ?? "Nada a notificar." });
      else setMsg({ tipo: "ok", texto: `E-mail enviado para ${r.enviadosPara?.join(", ")}.` });
      await carregar();
    } catch (err) {
      setMsg({ tipo: "err", texto: err instanceof ApiError ? err.message : "Falha ao enviar e-mail." });
    } finally {
      setEnviando(false);
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={carregar} />;
  if (loading && !data) return <div className="empty">Carregando…</div>;
  if (!data) return null;

  return (
    <div className="crud">
      <div className="alert-summary">
        <div className="alert-chip alert-chip-total">
          <strong>{data.total}</strong> itens críticos
        </div>
        <div className="alert-chip alert-chip-zero">
          <strong>{data.zerados}</strong> zerados
        </div>
        <div className="alert-chip alert-chip-low">
          <strong>{data.abaixo}</strong> abaixo do mínimo
        </div>
        <div className="alert-actions">
          <button className="btn-secondary" onClick={carregar} title="Atualizar">
            <IconRefresh size={16} /> Atualizar
          </button>
          <button className="btn-primary" onClick={notificar} disabled={enviando || data.total === 0}>
            <IconMail size={16} /> {enviando ? "Enviando…" : "Enviar aviso por e-mail"}
          </button>
        </div>
      </div>

      <div className={`alert-email-state ${data.emailConfigurado ? "is-on" : "is-off"}`}>
        {data.emailConfigurado ? (
          <>
            E-mail automático <strong>ATIVO</strong> → {data.destinatarios.length ? data.destinatarios.join(", ") : "destinatários configurados"}.
            {data.ultimoEnvio
              ? ` Último envio: ${new Date(data.ultimoEnvio.created_at).toLocaleString("pt-BR")} (${data.ultimoEnvio.origem ?? "—"}).`
              : " Nenhum envio ainda."}
          </>
        ) : (
          <>
            E-mail automático <strong>desligado</strong>. Preencha <code>SMTP_HOST</code>, <code>SMTP_USER</code>,{" "}
            <code>SMTP_PASS</code> e <code>ALERT_EMAILS</code> no <code>.env</code> do Almoxarifado para ativar o envio
            automático. O alerta visual abaixo continua funcionando.
          </>
        )}
      </div>

      {msg && (
        <div className={msg.tipo === "ok" ? "form-ok" : "form-error"}>
          {msg.tipo === "ok" && <IconCheck size={16} />} {msg.texto}
        </div>
      )}

      <div className="table-wrap">
        {data.itens.length === 0 ? (
          <div className="empty">Tudo certo — nenhum item abaixo do mínimo ou zerado. ✓</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Descrição</th>
                <th>Categoria</th>
                <th>Saldo</th>
                <th>Mínimo</th>
                <th>Local</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {data.itens.map((i) => (
                <tr key={i.id}>
                  <td>{i.codigo || "—"}</td>
                  <td>{i.descricao}</td>
                  <td>{i.categoria || "—"}</td>
                  <td>
                    <strong>{i.saldo}</strong> {i.unidade}
                  </td>
                  <td>{i.estoque_minimo ?? "—"}</td>
                  <td>{i.localizacao || "—"}</td>
                  <td>
                    <span className={`pill pill-${statusTone(i.situacao)}`}>{i.situacao}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
